/**
 * OAuth Callback Handler Page
 * Processes OAuth redirects from Google and other OAuth providers
 * Epic #87: Core Platform MVP - Shared Authentication Foundation
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loading, InlineNotification } from '@carbon/react';
import { useAuthStore } from '../../store/authStore';

const OAuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processOAuthCallback = async () => {
      try {
        // Check for error in query params
        const urlParams = new URLSearchParams(window.location.search);
        const errorParam = urlParams.get('error');
        const errorMessage = urlParams.get('message');

        if (errorParam) {
          setError(errorMessage || 'OAuth authentication failed');
          setTimeout(() => {
            navigate('/auth/login');
          }, 3000);
          return;
        }

        // Extract tokens from URL hash (backend redirects with #access_token=...&refresh_token=...&role=...)
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);

        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        const role = params.get('role');

        if (!accessToken) {
          setError('Invalid OAuth response - missing access token');
          setTimeout(() => {
            navigate('/auth/login');
          }, 3000);
          return;
        }

        // Store tokens
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('auth_token', accessToken);
        localStorage.setItem('sigma08_jwt_token', accessToken);

        if (refreshToken) {
          localStorage.setItem('refresh_token', refreshToken);
        }

        // Decode token to get user info
        try {
          const payload = JSON.parse(atob(accessToken.split('.')[1]));

          const user = {
            user_id: payload.user_id,
            email: payload.email,
            role: payload.role,
            subscription_type: payload.subscription_type,
            permissions: payload.permissions || [],
            username: payload.email.split('@')[0],
            family_id: payload.family_id || '',
            age_verified: true,
            mfa_enabled: false,
            first_name: payload.first_name || '',
            last_name: payload.last_name || '',
            avatar_url: payload.profile_image_url || '',
          };

          // Update auth store
          setUser(user);

          // Navigate to role-based dashboard
          const roleBasedRoute = {
            admin: '/admin',
            platform_admin: '/admin',
            teacher: '/teacher',
            student: '/student',
            parent: '/parent',
            principal: '/dashboard',
            district_admin: '/admin',
            individual: '/dashboard',
            enterprise_user: '/dashboard',
          };

          const targetRoute = roleBasedRoute[user.role] || '/dashboard';

          // Small delay to ensure token storage completes
          await new Promise(resolve => setTimeout(resolve, 500));

          navigate(targetRoute, { replace: true });
        } catch (decodeError) {
          console.error('[OAUTH CALLBACK] Failed to decode token:', decodeError);
          setError('Invalid OAuth token format');
          setTimeout(() => {
            navigate('/auth/login');
          }, 3000);
        }
      } catch (error) {
        console.error('[OAUTH CALLBACK] Error processing callback:', error);
        setError('Failed to process OAuth callback');
        setTimeout(() => {
          navigate('/auth/login');
        }, 3000);
      }
    };

    processOAuthCallback();
  }, [navigate, setUser]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        background: 'var(--cds-layer-01)',
      }}
    >
      {error ? (
        <InlineNotification kind="error" title="OAuth Error" subtitle={error} style={{ maxWidth: 600 }} />
      ) : (
        <>
          <Loading description="Processing authentication..." withOverlay={false} />
          <p
            style={{
              marginTop: '2rem',
              color: 'var(--cds-text-secondary)',
              textAlign: 'center',
            }}
          >
            Completing sign in...
          </p>
        </>
      )}
    </div>
  );
};

export default OAuthCallbackPage;
